import { useEffect, useState, type FormEvent } from 'react';
import { Page } from '../components/Page.js';
import { Attachments } from '../components/Attachments.js';

type Entry = {
  uuid: string; category: string; label: string; url: string | null;
  username: string | null; password: string | null; username_hint?: string | null;
  has_password?: number; status: string; confirmed_active: number; notes: string | null; ledger_seq: number;
  account_created_on?: string | null;
};

const CATS = [
  { key: 'cloud', title: 'Cloud storage' },
  { key: 'login', title: 'Online logins' },
  { key: 'social', title: 'Social media' },
] as const;

type EditForm = { category: string; label: string; url: string; username: string; password: string; notes: string; account_created_on: string; };

async function jget(u: string) { return fetch(u, { cache: 'no-store' }).then((r) => r.json()); }
async function jpost(u: string, body: unknown) {
  const r = await fetch(u, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  return { ok: r.ok, body: await r.json() };
}
async function jpatch(u: string, body: unknown) {
  const r = await fetch(u, { method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  return { ok: r.ok, body: await r.json() };
}

function blankEdit(e: Entry): EditForm {
  return { category: e.category, label: e.label, url: e.url ?? '', username: e.username ?? '', password: '', notes: e.notes ?? '', account_created_on: e.account_created_on ?? '' };
}

export function Logins() {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editingEntry, setEditingEntry] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EditForm | null>(null);
  const [showPw, setShowPw] = useState<Set<string>>(new Set());

  const load = async () => {
    try { setEntries((await jget('/api/vault')).entries ?? []); } catch { /* offline */ }
  };
  useEffect(() => { load(); }, []);

  async function eraseEntry(uuid: string, label: string) {
    if (!confirm(`Erase "${label}"? This crypto-shreds the credential — it cannot be recovered.`)) return;
    setErr(null);
    const { ok, body } = await jpost(`/api/vault/${uuid}/erase`, {});
    if (!ok) return setErr(body.error ?? 'Failed');
    load();
  }

  async function saveEntry(uuid: string) {
    if (!editForm) return;
    setErr(null);
    const patch: Record<string, unknown> = {
      category: editForm.category, label: editForm.label,
      url: editForm.url || null, notes: editForm.notes || null,
    };
    if (editForm.username !== '') patch['username'] = editForm.username;
    if (editForm.password !== '') patch['password'] = editForm.password;
    if (editForm.category === 'cloud') patch['account_created_on'] = editForm.account_created_on || null;
    const { ok, body } = await jpatch(`/api/vault/${uuid}`, patch);
    if (!ok) return setErr(body.error ?? 'Failed');
    setEditingEntry(null); setEditForm(null);
    load();
  }

  function togglePw(uuid: string) {
    setShowPw((prev) => {
      const next = new Set(prev);
      next.has(uuid) ? next.delete(uuid) : next.add(uuid);
      return next;
    });
  }

  const active = entries.filter((e) => e.status !== 'erased');

  function renderEntry(e: Entry) {
    const isEditing = editingEntry === e.uuid;
    if (isEditing && editForm) {
      const f = editForm;
      const set = (k: keyof EditForm) => (ev: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
        setEditForm({ ...f, [k]: ev.target.value });
      return (
        <div key={e.uuid} style={{ background: 'var(--surface2)', borderRadius: 6, padding: 12, marginTop: 8 }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <label className="fld" style={{ width: 140 }}><span>Category</span>
              <select value={f.category} onChange={set('category')}>
                <option value="cloud">Cloud storage</option>
                <option value="login">Online login</option>
                <option value="social">Social media</option>
              </select>
            </label>
            <label className="fld" style={{ flex: 1 }}><span>Label / service</span>
              <input value={f.label} onChange={set('label')} />
            </label>
          </div>
          <label className="fld"><span>URL (optional)</span>
            <input value={f.url} onChange={set('url')} placeholder="https://…" />
          </label>
          <div style={{ display: 'flex', gap: 10 }}>
            <label className="fld" style={{ flex: 1 }}><span>Username / email</span>
              <input value={f.username} onChange={set('username')} autoComplete="off" />
            </label>
            <label className="fld" style={{ flex: 1 }}><span>Password (leave blank to keep)</span>
              <input value={f.password} onChange={set('password')} autoComplete="off" placeholder="Leave blank to keep existing" />
            </label>
          </div>
          <label className="fld"><span>Notes</span>
            <textarea value={f.notes} onChange={set('notes')} rows={2} />
          </label>
          {f.category === 'cloud' && (
            <label className="fld"><span>Date account created (cloud storage)</span>
              <input type="date" value={f.account_created_on} onChange={set('account_created_on')} />
              <small className="muted3" style={{ fontSize: 11 }}>Set this only for a newly-created account — it starts a 3-day reminder to notify the PPU (SHPO §10). Leave blank for accounts that already existed.</small>
            </label>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <button className="btn btn-primary" onClick={() => saveEntry(e.uuid)}>Save</button>
            <button className="btn btn-ghost" onClick={() => { setEditingEntry(null); setEditForm(null); }}>Cancel</button>
          </div>
        </div>
      );
    }
    const pwVisible = showPw.has(e.uuid);
    return (
      <div key={e.uuid} style={{ borderTop: '1px solid var(--border)', paddingTop: 8, marginTop: 8 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <span className="badge badge-mute">{e.category.toUpperCase()}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="ledger-text">
              <strong>{e.label}</strong>
              {e.url ? <span className="muted3 mono"> · {e.url}</span> : null}
            </div>
            {e.username && <div className="ledger-time mono">{e.username}</div>}
            {e.password !== null && (
              <div className="ledger-time" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span className="mono">{pwVisible ? e.password : '••••••••'}</span>
                <button className="btn btn-ghost" style={{ fontSize: 11, padding: '1px 6px' }} onClick={() => togglePw(e.uuid)}>
                  {pwVisible ? 'Hide' : 'Show'}
                </button>
              </div>
            )}
            {e.notes && <div className="muted3" style={{ fontSize: 12, marginTop: 2 }}>{e.notes}</div>}
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <Attachments entityType="vault" entityUuid={e.uuid} compact />
            <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => { setEditingEntry(e.uuid); setEditForm(blankEdit(e)); }}>Edit</button>
            <button className="btn btn-ghost" style={{ fontSize: 12, color: 'var(--bad)' }} onClick={() => eraseEntry(e.uuid, e.label)}>Erase</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Page
      eyebrow="Records"
      title="Online accounts & passwords"
      actions={<span className="pill pill-ok"><span className="dot dot-ok" />encrypted · excluded from inspector &amp; PPU export</span>}
    >
      {err && <div style={{ color: 'var(--bad)', fontSize: 13, marginBottom: 10 }}>{err}</div>}

      <section className="card card-pad" style={{ marginBottom: 16 }}>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          A private, encrypted store for online accounts — logins, social-media and cloud accounts.
          Usernames and passwords are AES-256-GCM encrypted at rest.
          This section is <b>deliberately excluded</b> from inspector access and from the PPU proof
          packet. Erasing an entry crypto-shreds the secret while keeping a tamper-evident record.
        </p>
      </section>

      {CATS.map((c) => {
        const items = active.filter((e) => e.category === c.key);
        return (
          <div className="card card-pad" key={c.key} style={{ marginBottom: 12 }}>
            <div className="panel-head"><h3>{c.title}</h3><span className="hint">{items.length}</span></div>
            {c.key === 'social' && items.length > 0 && (
              <p className="muted" style={{ fontSize: 12.5, margin: '0 0 8px', lineHeight: 1.45 }}>
                Recorded for information and transparency only. These accounts pre-date the conviction and
                are <b>not accessed or used</b>, in line with the social-media restriction under the SHPO.
                They are listed to ensure full compliance.
              </p>
            )}
            {items.length === 0 && <div className="muted" style={{ fontSize: 13 }}>None.</div>}
            {items.map(renderEntry)}
          </div>
        );
      })}

      <div style={{ marginBottom: 8 }}>
        <button className="btn btn-ghost" onClick={() => setShowAdd((v) => !v)}>
          {showAdd ? '− Hide add account' : '+ Add account'}
        </button>
      </div>
      {showAdd && <AddForm onDone={() => { setShowAdd(false); load(); }} />}

      <p className="muted" style={{ fontSize: 12, marginTop: 14 }}>
        Usernames and passwords are encrypted (AES-256-GCM) at rest and never included in exports.
      </p>
    </Page>
  );
}

function AddForm({ onDone }: { onDone: () => void }) {
  const [f, setF] = useState({ category: 'login', label: '', url: '', username: '', password: '', notes: '', account_created_on: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!f.label.trim()) { setErr('Label required'); return; }
    setBusy(true);
    try {
      const res = await fetch('/api/vault', {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify(f),
      });
      if (!res.ok) { setErr((await res.json()).error ?? 'Failed'); return; }
      onDone();
    } catch { setErr('Could not reach the server'); } finally { setBusy(false); }
  }

  return (
    <div className="card card-pad" style={{ marginBottom: 12 }}>
      <div className="panel-head"><h3>Add an account</h3><span className="hint">encrypted</span></div>
      <form onSubmit={submit}>
        <div style={{ display: 'flex', gap: 10 }}>
          <label className="fld" style={{ width: 140 }}><span>Category</span>
            <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
              <option value="cloud">Cloud storage</option>
              <option value="login">Online login</option>
              <option value="social">Social media</option>
            </select>
          </label>
          <label className="fld" style={{ flex: 2 }}><span>Service / label</span>
            <input value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="e.g. wordpress.com" />
          </label>
          <label className="fld" style={{ flex: 2 }}><span>URL (optional)</span>
            <input value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="https://…" />
          </label>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <label className="fld" style={{ flex: 1 }}><span>Username / email</span>
            <input value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} autoComplete="off" />
          </label>
          <label className="fld" style={{ flex: 1 }}><span>Password</span>
            <input value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} autoComplete="off" />
          </label>
        </div>
        <label className="fld"><span>Notes (optional)</span>
          <textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} />
        </label>
        {f.category === 'cloud' && (
          <label className="fld"><span>Date account created (optional)</span>
            <input type="date" value={f.account_created_on} onChange={(e) => setF({ ...f, account_created_on: e.target.value })} />
            <small className="muted3" style={{ fontSize: 11 }}>Set only for a newly-created cloud account — starts a 3-day reminder to notify the PPU (SHPO §10). Leave blank for accounts that already existed.</small>
          </label>
        )}
        {err && <div style={{ color: 'var(--bad)', fontSize: 12.5, margin: '4px 0' }}>{err}</div>}
        <button className="btn btn-primary" type="submit" disabled={busy} style={{ width: '100%', justifyContent: 'center' }}>
          {busy ? 'Saving…' : 'Save (encrypted)'}
        </button>
      </form>
    </div>
  );
}
